import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Clock, X, BookOpen, Star, Sparkles, FileText, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import EventRegistrationModal from '../components/EventRegistrationModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  const [isPreviousEventsModalOpen, setIsPreviousEventsModalOpen] = useState(false);
  const [isCurrentEventsModalOpen, setIsCurrentEventsModalOpen] = useState(false);
  const [isFutureEventsModalOpen, setIsFutureEventsModalOpen] = useState(false);
  const [eventDetailModal, setEventDetailModal] = useState({ isOpen: false, event: null });
  const [selectedCurrentEventId, setSelectedCurrentEventId] = useState(null);
  const [events, setEvents] = useState({ previous: [], current: [], future: [] });
  const [loading, setLoading] = useState(true);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('');
  const { t, isRTL } = useLanguage();

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const openEventDetail = (event) => {
    setEventDetailModal({ isOpen: true, event });
  };

  const closeEventDetail = () => {
    setEventDetailModal({ isOpen: false, event: null });
  };

  const handleCurrentEventSelection = (eventId) => {
    setIsCurrentEventsModalOpen(false);
    setSelectedCurrentEventId(eventId);
    setTimeout(() => {
      const eventSection = document.getElementById('current-events-display');
      if (eventSection) {
        eventSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleRegisterClick = (eventName) => {
    setSelectedEvent(eventName);
    setIsRegistrationModalOpen(true);
  };

  const eventsToDisplay = selectedCurrentEventId 
    ? events.current.filter(event => event.id === selectedCurrentEventId)
    : events.current;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-80 bg-cover bg-center"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/7446988/pexels-photo-7446988.jpeg?w=1920&q=80)' }}
      >
        <div className="absolute inset-0 bg-slate-900 bg-opacity-60 flex items-center justify-center">
          <img 
            src="/logo.png" 
            alt="Target Health" 
            className="absolute bottom-6 left-6 w-40 opacity-20"
          />
          <h1 className="text-5xl font-bold text-white z-10">{t.events.title}</h1>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-800 mb-2">
              {isRTL ? t.events.mainTitle : 'HEALTHCARE'} <span className="text-orange-500">{isRTL ? '' : 'EVENTS'}</span>
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-4"></div>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {isRTL ? 'انضم إلى فعالياتنا الصحية واكتشف أحدث الابتكارات' : 'Join our healthcare events and discover the latest innovations'}
            </p>
          </div>

          {/* Previous, Current and Future Events Boxes */}
          <div className="max-w-6xl mx-auto mb-12 grid md:grid-cols-3 gap-6">
            {/* Previous Events Box */}
            <button
              onClick={() => setIsPreviousEventsModalOpen(true)}
              className="group bg-gradient-to-br from-slate-100 to-slate-200 hover:from-purple-50 hover:to-purple-100 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-slate-600 group-hover:bg-purple-500 rounded-full p-6 transition-colors">
                  <BookOpen size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-purple-600">
                  {isRTL ? 'الفعاليات السابقة' : 'Previous Events'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'عرض أرشيف الفعاليات المنجزة' : 'View archive of past events'}
                </p>
                <span className="bg-slate-600 group-hover:bg-purple-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {events.previous.length} {isRTL ? 'فعاليات' : 'Events'}
                </span>
              </div>
            </button>

            {/* Current Events Box */}
            <button
              onClick={() => setIsCurrentEventsModalOpen(true)}
              className="group bg-gradient-to-br from-orange-100 to-orange-200 hover:from-orange-200 hover:to-orange-300 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-orange-500 group-hover:bg-orange-600 rounded-full p-6 transition-colors">
                  <Calendar size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-orange-700">
                  {isRTL ? 'الفعاليات الحالية' : 'Current Events'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'عرض الفعاليات الجارية' : 'View ongoing events'}
                </p>
                <span className="bg-orange-500 group-hover:bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {events.current.length} {isRTL ? 'فعاليات' : 'Events'}
                </span>
              </div>
            </button>

            {/* Future Events Box */}
            <button
              onClick={() => setIsFutureEventsModalOpen(true)}
              className="group bg-gradient-to-br from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl p-8 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <div className={`flex flex-col items-center gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                <div className="bg-blue-500 group-hover:bg-blue-600 rounded-full p-6 transition-colors">
                  <Star size={40} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800 group-hover:text-blue-600">
                  {isRTL ? 'الفعاليات المستقبلية' : 'Future Events'}
                </h3>
                <p className="text-slate-600 text-sm">
                  {isRTL ? 'اكتشف الفعاليات القادمة' : 'Discover upcoming events'}
                </p>
                <span className="bg-blue-500 group-hover:bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  {events.future.length} {isRTL ? 'فعاليات' : 'Events'}
                </span>
              </div>
            </button>
          </div>

          {/* Current Events Display */}
          {eventsToDisplay.length > 0 ? (
            <div id="current-events-display" className="max-w-6xl mx-auto mb-16">
              <h3 className="text-3xl font-bold text-slate-800 mb-8 text-center">
                {isRTL ? 'الفعاليات الحالية' : 'Current'} <span className="text-orange-500">{isRTL ? '' : 'Events'}</span>
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {eventsToDisplay.map((event) => (
                  <div key={event.id} className="bg-white rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                    {event.image && (
                      <div className="w-full h-48 bg-slate-100 flex items-center justify-center">
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <span className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                        {event.category}
                      </span>
                      <h4 className={`text-2xl font-bold text-slate-800 mb-3 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && event.titleArabic ? event.titleArabic : event.title}
                      </h4>
                      <p className={`text-slate-700 mb-4 ${isRTL ? 'text-right' : ''}`}>
                        {isRTL && event.descriptionArabic ? event.descriptionArabic : event.description}
                      </p>

                      <div className="space-y-2 mb-4">
                        <div className={`flex items-center gap-2 text-slate-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar size={18} className="text-orange-500" />
                          <span>{isRTL && event.dateArabic ? event.dateArabic : event.date}</span>
                        </div>
                        <div className={`flex items-center gap-2 text-slate-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <MapPin size={18} className="text-orange-500" />
                          <span>{isRTL && event.locationArabic ? event.locationArabic : event.location}</span>
                        </div>
                        {event.capacity && (
                          <div className={`flex items-center gap-2 text-slate-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Users size={18} className="text-orange-500" />
                            <span>{event.capacity}</span>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => handleRegisterClick(isRTL && event.titleArabic ? event.titleArabic : event.title)}
                        className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-center px-6 py-3 rounded-lg font-bold transition-all transform hover:scale-105"
                      >
                        📧 {isRTL ? 'إرسال طلب' : 'Send Request'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto text-center py-20">
              <div className="bg-slate-50 rounded-lg p-16">
                <Calendar size={64} className="text-slate-300 mx-auto mb-4" />
                <h3 className="text-6xl font-bold text-slate-300 mb-4">{t.events.comingSoon}</h3>
                <p className="text-slate-600 text-lg">
                  {t.events.comingSoonText}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Previous Events Modal */}
      {isPreviousEventsModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsPreviousEventsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-purple-500 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <BookOpen size={28} />
                <h3 className="text-2xl font-bold">{isRTL ? 'الفعاليات السابقة' : 'Previous Events'}</h3>
              </div>
              <button
                onClick={() => setIsPreviousEventsModalOpen(false)}
                className="hover:bg-purple-600 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              {events.previous.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {events.previous.map((event) => (
                    <div 
                      key={event.id}
                      className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                      onClick={() => {
                        setIsPreviousEventsModalOpen(false);
                        openEventDetail(event);
                      }}
                    >
                      {event.image && (
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-full h-40 object-contain group-hover:scale-105 transition-transform"
                        />
                      )}
                      <div className="p-5">
                        <span className="inline-block bg-slate-200 text-slate-600 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                          {isRTL ? 'منتهي' : 'Completed'}
                        </span>
                        <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-purple-600 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.titleArabic ? event.titleArabic : event.title}
                        </h4>
                        <p className={`text-slate-600 text-sm mb-3 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.descriptionArabic ? event.descriptionArabic : event.description}
                        </p>
                        <div className={`flex items-center gap-2 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar size={14} />
                          <span>{isRTL && event.dateArabic ? event.dateArabic : event.date}</span>
                        </div>
                        <div className="mt-3 text-purple-500 group-hover:text-purple-600 font-semibold text-sm">
                          {isRTL ? 'عرض التفاصيل ←' : 'View Details →'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-500">{isRTL ? 'لا توجد فعاليات سابقة' : 'No previous events yet'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current Events Modal */}
      {isCurrentEventsModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsCurrentEventsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-orange-500 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Calendar size={28} />
                <h3 className="text-2xl font-bold">{isRTL ? 'الفعاليات الحالية' : 'Current Events'}</h3>
              </div>
              <button
                onClick={() => setIsCurrentEventsModalOpen(false)}
                className="hover:bg-orange-600 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              {events.current.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {events.current.map((event) => (
                    <div 
                      key={event.id}
                      className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                      onClick={() => handleCurrentEventSelection(event.id)}
                    >
                      {event.image && (
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-full h-40 object-contain group-hover:scale-105 transition-transform"
                        />
                      )}
                      <div className="p-5">
                        <span className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
                          {isRTL ? 'نشط الآن' : 'Active Now'}
                        </span>
                        <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-orange-600 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.titleArabic ? event.titleArabic : event.title}
                        </h4>
                        <p className={`text-slate-600 text-sm mb-3 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.descriptionArabic ? event.descriptionArabic : event.description}
                        </p>
                        <div className={`flex items-center gap-2 text-sm text-slate-500 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar size={14} />
                          <span>{isRTL && event.dateArabic ? event.dateArabic : event.date}</span>
                        </div>
                        <div className={`flex items-center gap-2 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <MapPin size={14} />
                          <span>{isRTL && event.locationArabic ? event.locationArabic : event.location}</span>
                        </div>
                        <div className="mt-3 text-orange-500 group-hover:text-orange-600 font-semibold text-sm">
                          {isRTL ? 'اختر هذه الفعالية →' : 'Select This Event →'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-500">{isRTL ? 'لا توجد فعاليات حالية' : 'No current events'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Future Events Modal */}
      {isFutureEventsModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFutureEventsModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-blue-500 text-white p-6 rounded-t-lg flex items-center justify-between flex-shrink-0">
              <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Star size={28} />
                <h3 className="text-2xl font-bold">{isRTL ? 'الفعاليات المستقبلية' : 'Future Events'}</h3>
              </div>
              <button
                onClick={() => setIsFutureEventsModalOpen(false)}
                className="hover:bg-blue-600 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="overflow-y-auto p-6 flex-1">
              {events.future.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {events.future.map((event) => (
                    <div 
                      key={event.id}
                      className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer group"
                      onClick={() => {
                        setIsFutureEventsModalOpen(false);
                        openEventDetail(event);
                      }}
                    >
                      {event.image && (
                        <img 
                          src={event.image} 
                          alt={event.title}
                          className="w-full h-40 object-contain group-hover:scale-105 transition-transform"
                        />
                      )}
                      <div className="p-5">
                        <span className="inline-block bg-blue-200 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                          {isRTL ? 'قريباً' : 'Coming Soon'}
                        </span>
                        <h4 className={`text-lg font-bold text-slate-800 mb-2 group-hover:text-blue-600 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.titleArabic ? event.titleArabic : event.title}
                        </h4>
                        <p className={`text-slate-600 text-sm mb-3 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                          {isRTL && event.descriptionArabic ? event.descriptionArabic : event.description}
                        </p>
                        <div className={`flex items-center gap-2 text-sm text-slate-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <Calendar size={14} />
                          <span>{isRTL && event.dateArabic ? event.dateArabic : event.date}</span>
                        </div>
                        <div className="mt-3 text-blue-500 group-hover:text-blue-600 font-semibold text-sm">
                          {isRTL ? 'عرض التفاصيل ←' : 'View Details →'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-slate-500">{isRTL ? 'لا توجد فعاليات مستقبلية' : 'No future events yet'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Modal */}
      {eventDetailModal.isOpen && eventDetailModal.event && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closeEventDetail}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex-shrink-0 bg-slate-100">
              {eventDetailModal.event.image && (
                <div className="w-full h-80 flex items-center justify-center">
                  <img 
                    src={eventDetailModal.event.image} 
                    alt={eventDetailModal.event.title}
                    className="max-w-full max-h-80 object-contain rounded-t-lg"
                  />
                </div>
              )}
              <button
                onClick={closeEventDetail}
                className="absolute top-4 right-4 bg-white hover:bg-slate-100 rounded-full p-2 shadow-lg transition-colors"
              >
                <X size={24} className="text-slate-800" />
              </button>
            </div>

            <div className="overflow-y-auto p-8 flex-1">
              <div className="mb-4">
                <span className="inline-block bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                  {eventDetailModal.event.category}
                </span>
              </div>
              
              <h2 className={`text-3xl font-bold text-slate-800 mb-4 ${isRTL ? 'text-right' : ''}`}>
                {isRTL && eventDetailModal.event.titleArabic ? eventDetailModal.event.titleArabic : eventDetailModal.event.title}
              </h2>

              <p className={`text-slate-700 text-lg mb-6 leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                {isRTL && eventDetailModal.event.descriptionArabic ? eventDetailModal.event.descriptionArabic : eventDetailModal.event.description}
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-orange-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Calendar size={20} />
                    <span className="font-semibold">{isRTL ? 'التاريخ' : 'Date'}</span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {isRTL && eventDetailModal.event.dateArabic ? eventDetailModal.event.dateArabic : eventDetailModal.event.date}
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className={`flex items-center gap-2 text-purple-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <MapPin size={20} />
                    <span className="font-semibold">{isRTL ? 'الموقع' : 'Location'}</span>
                  </div>
                  <p className={`text-slate-700 ${isRTL ? 'text-right' : ''}`}>
                    {isRTL && eventDetailModal.event.locationArabic ? eventDetailModal.event.locationArabic : eventDetailModal.event.location}
                  </p>
                </div>

                {eventDetailModal.event.organizer && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className={`flex items-center gap-2 text-blue-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Users size={20} />
                      <span className="font-semibold">{isRTL ? 'المنظم' : 'Organizer'}</span>
                    </div>
                    <p className="text-slate-700">{eventDetailModal.event.organizer}</p>
                  </div>
                )}

                {eventDetailModal.event.capacity && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className={`flex items-center gap-2 text-green-600 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Users size={20} />
                      <span className="font-semibold">{isRTL ? 'السعة' : 'Capacity'}</span>
                    </div>
                    <p className="text-slate-700">{eventDetailModal.event.capacity}</p>
                  </div>
                )}
              </div>

              {/* Download Brochures */}
              {eventDetailModal.event.brochures && eventDetailModal.event.brochures.length > 0 && (
                <div className="mb-6">
                  <h5 className={`font-bold text-slate-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <FileText size={20} className="text-cyan-500" />
                    {isRTL ? 'تحميل الملفات' : 'Download Materials'}
                  </h5>
                  <div className="space-y-2">
                    {eventDetailModal.event.brochures.map((brochure, index) => (
                      <a
                        key={index}
                        href={brochure.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors group ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="bg-red-500 p-2 rounded">
                            <Download size={20} className="text-white" />
                          </div>
                          <span className="font-semibold text-slate-700 group-hover:text-cyan-600">
                            {brochure.title}
                          </span>
                        </div>
                        <span className="text-slate-400 text-sm">PDF</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => {
                  closeEventDetail();
                  handleRegisterClick(isRTL && eventDetailModal.event.titleArabic ? eventDetailModal.event.titleArabic : eventDetailModal.event.title);
                }}
                className="block w-full bg-orange-500 hover:bg-orange-600 text-white text-center px-6 py-3 rounded-lg font-semibold mb-4 transition-colors"
              >
                📧 {isRTL ? 'إرسال طلب' : 'Send Request'}
              </button>

              <button
                onClick={closeEventDetail}
                className="w-full bg-slate-600 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                {isRTL ? 'إغلاق' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Registration Modal */}
      <EventRegistrationModal 
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        eventName={selectedEvent}
      />
    </div>
  );
};

export default Events;
